import {
  useCallback,
  useRef,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from "react";
import {
  Eyebrow,
  InfoIcon,
  PointIcon,
  StatusPill,
  StickerIcon,
} from "../../../shared/components";
import type { AttendanceState } from "../../../shared/api/parent";
import type { AttendanceCalendar, AttendanceMonth } from "./attendanceCalendar";
import { GAME_FOR_SKILL, MOTOR_TINT } from "../../../shared/ui/gameArt";
import type { ContentSection, MotorSection } from "./skillSections";
import { PARENT_COPY, type Lang } from "./copy";

/**
 * The parent tier's playful surface — the child's own world, on the adult screen
 * that is about them.
 *
 * 🔴 THE PLAYFULNESS CARRIES INFORMATION, WHICH IS THE ONLY REASON IT IS ALLOWED
 * HERE. design-system.md draws a hard line: DashboardShell's warmth comes from
 * *"rounded corners, generous spacing, warm neutrals and a rounded typeface —
 * **never from cartoon elements**"*, and §12 forbids emoji as UI icons outright.
 * A friendly cartoon dropped onto a parent's report to soften it is exactly that
 * rule's target.
 *
 * So nothing below is decoration. Every picture on this screen is a picture *of
 * the child's week*: the plate they tapped to start Sky Quest, the sticker they
 * were given, the mascot who narrates Turo's World. A parent who cannot read a
 * bar chart can still read "they played the bird game and got three stars", and
 * that is the 30-second read C1 is specified as.
 *
 * ⚠️ WHAT IS DELIBERATELY NOT HERE: no emoji, no invented mascot, no confetti,
 * no colour picked outside §3's fixed skill map, and no number rendered in a
 * child's typeface. Baloo appears exactly once — on the child's own name — and
 * every figure on the screen stays Nunito and tabular.
 *
 * 🔴 NOT ONE COMPONENT HERE ASKS WHETHER IT MAY ANIMATE. The pointer handlers
 * write `--mx`/`--my`/`--tx`/`--ty` and stop; `play.css` decides whether a
 * transform is built from them, under the same two reduce-motion switches
 * `kid.css` uses. See `useReduceMotion.ts` — *"the moment a component reads this
 * value, the rule has to be re-implemented in every component and one of them
 * will get it wrong"*.
 */

/* ────────────────────────────────────────────── the pointer, as two numbers ── */

/**
 * Where the cursor is inside this element, as CSS custom properties.
 *
 * ⚠️ WRITTEN STRAIGHT TO THE NODE, NOT HELD IN STATE. A `useState` here would
 * re-render the card — and everything under it — on every mouse move, on the one
 * tier that is also the most likely to be open on a five-year-old laptop. The
 * values are presentation-only and nothing reads them back, which is exactly the
 * case a ref is for.
 */
function usePointerVars(): {
  ref: React.RefObject<HTMLDivElement | null>;
  onMouseMove: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
} {
  const ref = useRef<HTMLDivElement | null>(null);

  const onMouseMove = useCallback((e: MouseEvent<HTMLElement>) => {
    const node = ref.current;
    if (!node) return;
    const box = node.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    /* 0–1 across the box, for the glow's position … */
    const x = (e.clientX - box.left) / box.width;
    const y = (e.clientY - box.top) / box.height;
    node.style.setProperty("--mx", `${String(Math.round(x * 100))}%`);
    node.style.setProperty("--my", `${String(Math.round(y * 100))}%`);
    /* … and −1–1 from the centre, for anything that tilts. */
    node.style.setProperty("--tx", (x * 2 - 1).toFixed(3));
    node.style.setProperty("--ty", (y * 2 - 1).toFixed(3));
  }, []);

  const onMouseLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    /* Back to rest, rather than frozen wherever the pointer left. */
    node.style.setProperty("--tx", "0");
    node.style.setProperty("--ty", "0");
  }, []);

  return { ref, onMouseMove, onMouseLeave };
}

/* ───────────────────────────────────────────────────────────── the hero ── */

export interface ChildHeroProps {
  lang: Lang;
  /** The `<h1>`. 🔴 The screen's subject is the child, not the word "Progress". */
  childName: string;
  className: string | null;
  /**
   * 🔴 THE CHILD'S OWN TWO NUMBERS, AND DELIBERATELY NOT THE WEEK'S. Games,
   * sessions and the streak are counts of a period and belong in the row of stat
   * cards below, where a parent compares them week to week. Coins and stickers
   * are what the child would say they *have* — so they ride up here with the
   * name, and neither number appears twice on the screen.
   */
  coins: number;
  stickers: number;
  /** The screen's own controls — "Progress detail". */
  actions?: ReactNode;
}

/**
 * C1's header, as the one warm band on the screen.
 *
 * 🔴 THE `<h1>` IS THE CHILD'S NAME, AND THAT IS A CORRECTION RATHER THAN A
 * FLOURISH. It used to be "Progress" with the name as a subtitle — the same word
 * the sidebar already highlights, which is precisely the duplication `Shell`'s
 * own `title` prop was deleted for. The eyebrow above carries the section name
 * for anyone who needs it, and §4 permits exactly one eyebrow, above a page
 * title. Exactly one `<h1>` is still rendered per document.
 *
 * ⚠️ AND IT IS THE ONE STRING IN THE ADULT TIER SET IN BALOO. The child's own
 * name in the child's own typeface is a deliberate, single exception; §4's
 * split — Baloo for children, Nunito for adults — holds for every other
 * character on the screen, and most of all for the numbers.
 */
export function ChildHero({
  lang,
  childName,
  className,
  coins,
  stickers,
  actions,
}: ChildHeroProps) {
  const t = PARENT_COPY[lang];
  const pointer = usePointerVars();

  return (
    <div
      ref={pointer.ref}
      onMouseMove={pointer.onMouseMove}
      onMouseLeave={pointer.onMouseLeave}
      /* ⚠️ NO `play-card` AND NO GLOW ON THIS ONE. The header is not a thing to
         point at — it lifts nothing and opens nothing — so the pointer it tracks
         drives Turo's tilt and nothing else. A band this wide with a light
         following the cursor across it would be the whole screen moving. */
      className="play-rise relative mb-6 overflow-hidden rounded-xl border border-line bg-surface shadow-card"
    >
      {/*
        The warm ground behind the band. A tint of the brand rather than a
        picture, so the only artwork on this card is Turo — §2's `primary-tint`
        is the token for exactly this, and it keeps the contrast of everything
        on it.

        ✅ ONE FLAT COLOUR, NOT A GRADIENT. It used to ramp `primary-tint →
        cream → surface` across 105°, which put the three things on this band —
        the name, the two chips and the button — on three different
        backgrounds: the chips are `bg-surface/80`, so on the left they sat on
        pink and by the right-hand end they had dissolved into a ground of
        their own colour. A single tint gives every element on the band the
        same contrast wherever it lands, which is the only reason the tint was
        chosen over a picture in the first place.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: "var(--color-primary-tint)" }}
      />

      <div className="play-layer relative flex items-center gap-5 px-6 py-5">
        <div className="min-w-0 flex-1">
          <Eyebrow>{t.dashboard}</Eyebrow>
          {/* `font-kid` — see the note above. Sized on the DASH scale, not the
              kid one: this is still an adult's screen. */}
          <h1 className="font-kid text-dash-2xl leading-none font-extrabold text-ink">
            {childName}
          </h1>
          {className ? (
            <p className="mt-1.5 text-dash-sm text-ink-soft">{className}</p>
          ) : null}

          {/*
            🔴 WHAT THE CHILD WOULD SAY THEY HAVE — B10's coins and stickers, the
            two things they earn and keep. The coin turns over when the pointer
            is on it, which is the second of the two places `--ease-kid` is
            allowed in this tier and the one a child will find first.
          */}
          <ul className="mt-4 flex flex-wrap items-center gap-2">
            <li className="play-coin">
              <Chip tint="var(--color-sunshine)" label={t.coins} value={coins}>
                <span className="play-coin-face inline-flex">
                  <PointIcon size={15} />
                </span>
              </Chip>
            </li>
            <li>
              <Chip
                tint="var(--color-primary)"
                label={t.stickersEarned}
                value={stickers}
              >
                <StickerIcon size={15} />
              </Chip>
            </li>
          </ul>

          {actions ? (
            <div className="mt-4 flex flex-wrap gap-2">{actions}</div>
          ) : null}
        </div>

        {/*
          🔴 THE KALQY BOY, WHO IS PRODUCT ART AND NOT A CARTOON BORROWED TO LOOK
          FRIENDLY. He tilts toward the cursor — the one thing on this screen that
          is purely for the child reading it over their parent's shoulder, which
          is how this tier is actually used.

          ✅ HE REPLACED TURO, AND THE SWAP FIXED THE ONE THING THE OLD NOTE HERE
          GOT WRONG. It claimed *"a parent whose child plays it knows him by
          name"* — true of the turtle only for the one game of five he guides.
          For the other four he was a character nobody had met, dropped onto an
          adult's report to soften it, which is precisely the "cartoon element"
          §1 rules out. The boy is the product's own figure and he is already the
          artwork at the foot of this tier's sidebar (`.play-sidekick`), so the
          two pieces of art a parent sees are now one character rather than two
          strangers.

          ⚠️ `aria-hidden`, AND NO TEXT ALTERNATIVE ANYWHERE. He states nothing a
          screen reader needs; announcing him between a child's name and their
          coins would be noise on the one screen that must stay a 30-second read.
          That is also what makes him safe to drop entirely on a phone — nothing
          is lost, which a picture carrying information could not claim.
        */}
        <div className="play-float shrink-0" aria-hidden="true">
          {/* ⚠️ A BACKGROUND, NOT AN `<img>` — `play.css` explains why: he is
              288 KB of decoration that a phone would download and never draw. */}
          <div className="play-mascot play-tilt" />
        </div>
      </div>
    </div>
  );
}

/** One fact from the child's own world — an icon in its colour, a label, a count. */
function Chip({
  tint,
  label,
  value,
  children,
}: {
  tint: string;
  label: string;
  value: number;
  children: ReactNode;
}) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-pill border bg-surface/80 py-1.5 pr-3.5 pl-2.5 font-dash text-dash-xs font-bold text-ink-soft"
      style={{ borderColor: `color-mix(in srgb, ${tint} 35%, transparent)` }}
    >
      <span
        aria-hidden="true"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full"
        style={{
          background: `color-mix(in srgb, ${tint} 18%, transparent)`,
          color: tint,
        }}
      >
        {children}
      </span>
      {/* 🔴 The numeral is `ink` and tabular at every tone — §4, and `sunshine`
          fails contrast as text outright (§2). */}
      <span className="tabular text-dash-sm font-extrabold text-ink">
        {value}
      </span>
      <span className="font-normal">{label}</span>
    </span>
  );
}

/* ──────────────────────────────────────────────── what they are learning ── */

export interface LearningCardProps {
  section: ContentSection;
  lang: Lang;
  /** Position in the grid — drives the entrance stagger only. */
  index: number;
  children: ReactNode;
}

/**
 * One content skill, as the game that teaches it.
 *
 * 🔴 `SkillProgress` STILL DRAWS THE LEVEL, AND THIS DOES NOT. The component has
 * no `variant` prop *"so a caller cannot render Numeracy as a percentage even by
 * accident"* — a card that hand-drew its own segments would be that guarantee
 * quietly removed in favour of a nicer-looking box. This supplies the picture,
 * the name and the colour; the honest shape of the number stays where it was.
 *
 * ⚠️ A SKILL WITH NOTHING BEHIND IT KEEPS ITS CARD AND SAYS SO. `skillSections`
 * builds from the enum precisely so an untouched skill appears rather than
 * vanishes; a veiled plate with "Not started" on it reads as a game they have
 * not met yet, which is what it is — where a missing card would read as a
 * product with four games.
 */
export function LearningCard({
  section,
  lang,
  index,
  children,
}: LearningCardProps) {
  const t = PARENT_COPY[lang];
  const game = GAME_FOR_SKILL[section.skill];
  const pointer = usePointerVars();

  return (
    <div
      ref={pointer.ref}
      onMouseMove={pointer.onMouseMove}
      onMouseLeave={pointer.onMouseLeave}
      className="play-card play-rise relative overflow-hidden rounded-xl border border-line bg-surface shadow-card"
      style={
        {
          "--play-tint": game.tint,
          "--rise-i": index,
        } as CSSProperties
      }
    >
      <span className="play-glow" aria-hidden="true" />

      <div className="play-layer relative">
        {/*
          The plate the child taps to start this game. `aspect-[3/2]` and
          `object-cover` are `GameArt`'s own contract in the kid tier — same
          images, same crop, so a parent and their child are looking at the same
          picture.
        */}
        <div className="relative overflow-hidden">
          <img
            src={game.art}
            alt={game.title[lang]}
            className="play-art block aspect-[3/2] w-full object-cover select-none"
            loading="lazy"
            draggable={false}
          />
          {!section.started && (
            /* ⚠️ A WARM VEIL, NOT A GREYSCALE FILTER. §1: no grey, anywhere —
               and a desaturated plate would read as artwork that failed to
               load rather than as a game not yet played. */
            <span
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "color-mix(in srgb, var(--color-canvas) 72%, transparent)",
              }}
            />
          )}
          {!section.started && (
            <span className="absolute bottom-2 left-2">
              {/* §12 — icon + colour + text, all three, which `StatusPill`'s own
                  type requires rather than merely recommends. */}
              <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                {t.notStarted}
              </StatusPill>
            </span>
          )}
        </div>

        <div className="border-t border-line p-4">
          {/*
            The game's name above the skill's bar. The plate paints its own
            title as artwork, so this line is the text alternative for the
            picture — what a screen reader announces, and what shows if the
            plate does not load.
          */}
          <p
            className="mb-2.5 flex items-center gap-2 font-dash text-dash-xs font-bold"
            style={{ color: game.tint }}
          >
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: game.tint }}
            />
            {game.title[lang]}
          </p>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────── how they are moving ── */

export interface MovingRowProps {
  section: MotorSection;
  index: number;
  children: ReactNode;
}

/**
 * One motor skill: the bar, and the denominator under it.
 *
 * 🔴 THE CAPTION IS NOT OPTIONAL AND IS NOT STYLING. Contract D4 — *"81% of
 * what?"* — so the sentence that says "exercised in 8 of 10 sessions" travels
 * with the bar wherever the bar goes. It is the caller's string; this only gives
 * it a place that survives a redesign.
 */
export function MovingRow({ section, index, children }: MovingRowProps) {
  const tint = MOTOR_TINT[section.skill];
  return (
    <div
      className="play-rise flex flex-col gap-1"
      style={{ "--rise-i": index, "--play-tint": tint } as CSSProperties}
    >
      {children}
      <span className="text-dash-xs text-muted">{section.caption}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── the calendar ── */

/**
 * C6's 28 days, as squares.
 *
 * 🔴 IT REPLACED A ONE-COLUMN TABLE OF DATES, AND THE SWAP IS THE POINT.
 * `parent.service.ts` chose 28 days so a *pattern* would be visible — *"every
 * Monday"* — and a list of three dates cannot show one: you have to hold three
 * weekday names in your head and compare them. Seven to a row, every column is
 * the same weekday and the pattern is a vertical line.
 *
 * 🔴 AN UNMARKED SQUARE MEANS "NO ABSENCE RECORDED" AND THE LEGEND SAYS SO. It
 * does not mean present. A register nobody took writes no row, which is
 * identical in the data to a day the whole class came in — a grid of green ticks
 * would be the reassuring version of the one screen in this tier whose whole
 * purpose is to be the truthful one. That is also why there is no percentage
 * anywhere on it.
 *
 * ⚠️ AMBER, NOT RED. `AttendanceScreen` already refuses `tone="danger"` on the
 * count for a stated reason — *"three absences is a fact about a term, not an
 * alarm about a family"* — and a wall of red squares on a parent's phone about a
 * child who had flu would be that mistake at four times the size.
 */
/**
 * 🔴 THE FOUR STATES, AND THE COLOUR EACH IS ALLOWED.
 *
 * ⚠️ ABSENT IS `coral`, NOT `danger`. It is the red a parent asked for and it is
 * still the softer of the two reds in the token set — `AttendanceScreen` already
 * refuses `tone="danger"` on the count for a stated reason: *"three absences is
 * a fact about a term, not an alarm about a family"*, and a child who had flu
 * should not open their parent's phone to a wall of alarm red.
 *
 * ⚠️ NO_REGISTER KEEPS A COLOUR OF ITS OWN AND IT IS NOT A PALE GREEN. It is the
 * state that means nobody checked, and every visual nudge toward "probably fine"
 * is the screen answering a question the database cannot.
 */
const DAY_TONE: Record<
  AttendanceState,
  { bg: string; line: string; fg: string; dot: boolean }
> = {
  ABSENT: {
    bg: "color-mix(in srgb, var(--color-coral) 16%, transparent)",
    line: "color-mix(in srgb, var(--color-coral) 45%, transparent)",
    fg: "var(--color-coral)",
    dot: true,
  },
  PRESENT: {
    bg: "color-mix(in srgb, var(--color-leaf) 15%, transparent)",
    line: "color-mix(in srgb, var(--color-leaf) 40%, transparent)",
    /* The leaf token is a fill; `pill-success-fg` is its text-safe partner, and
       this numeral is text. */
    fg: "var(--color-pill-success-fg)",
    dot: false,
  },
  HOLIDAY: {
    bg: "color-mix(in srgb, var(--color-grape) 14%, transparent)",
    line: "color-mix(in srgb, var(--color-grape) 38%, transparent)",
    fg: "var(--color-grape)",
    dot: false,
  },
  NO_REGISTER: {
    bg: "var(--color-canvas)",
    line: "var(--color-line)",
    fg: "var(--color-muted)",
    dot: false,
  },
};

export interface AttendanceGridProps {
  calendar: AttendanceCalendar;
  lang: Lang;
  /**
   * ✅ THE SQUARES OPEN SOMETHING NOW, AND THAT CLOSED A REAL GAP.
   *
   * 🔴 A GRID OF TWENTY-EIGHT ROUNDED, COLOURED, TOOLTIPPED BOXES IS A ROW OF
   * CONTROLS WHATEVER IT IS MADE OF — a parent taps one, because it looks like a
   * thing that opens. Until now nothing happened, and the tooltip said only what
   * the colour already said. *"What did they actually do on Tuesday?"* is the
   * question the calendar itself puts in a parent's head, and the answer existed
   * in the database the whole time.
   *
   * ⚠️ OPTIONAL, SO THE GRID STAYS A GRID WHERE THERE IS NOTHING TO OPEN. Without
   * this the squares render exactly as they did — `<span>`s, no focus ring, no
   * pointer — rather than as buttons that do nothing, which is the worse of the
   * two failures.
   */
  onPick?: (day: string) => void;
  /** The open day, so the grid can say which square the panel below belongs to. */
  selected?: string | null;
}

export function AttendanceGrid({
  calendar,
  lang,
  onPick,
  selected = null,
}: AttendanceGridProps) {
  const t = PARENT_COPY[lang];

  /* The accessible name and the tooltip: one sentence per state, never a bare
     date — a square that announces "8 August" says nothing about the child. */
  const describe = (day: (typeof calendar.days)[number]): string => {
    switch (day.state) {
      case "ABSENT":
        return t.attendanceAbsentOn(day.label);
      case "PRESENT":
        return t.attendancePresentOn(day.label);
      case "HOLIDAY":
        return t.attendanceHolidayOn(
          day.label,
          day.holidayName ?? t.attendanceLegendHoliday,
        );
      default:
        return t.attendanceUnknownOn(day.label);
    }
  };

  return (
    /*
     * 🔴 THE CALENDAR IS CAPPED, AND THE SQUARES ARE WHY. Each day is
     * `aspect-square w-full` inside a seven-column grid, so a cell is one
     * seventh of whatever it is given — and this sits in a full-width `Panel`
     * inside the shell's 73.75rem measure. On a laptop that made ~160px squares
     * holding a two-digit number: a month of attendance that needed scrolling
     * to take in at once, which is the one thing the grid replaced a date list
     * to avoid (`parent.service.ts` picked 28 days so a pattern like "every
     * Monday" would be visible in one glance).
     *
     * ⚠️ A MAX-WIDTH, NOT A FIXED CELL SIZE. Below `md` a phone is narrower
     * than the cap, so the grid still fills the width it is given and the
     * squares stay thumb-sized — this tier is read on a phone first, and a
     * fixed 40px cell would have shrunk it there too.
     */
    <div className="w-full max-w-md">
      {/* Decorative: every square below carries its own full date, so reading
          these letters out would be announcing the same information twice. */}
      <div
        aria-hidden="true"
        className="mb-1.5 grid grid-cols-7 gap-1.5 text-center font-dash text-dash-xs font-bold text-muted"
      >
        {calendar.weekdays.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>

      <ul className="grid grid-cols-7 gap-1.5">
        {calendar.days.map((day, i) => {
          const tone = DAY_TONE[day.state];
          /* Only a day that says something gets a name and an entrance. A
             screen reader running the grid hears the absences, the holidays and
             the days a register confirmed — and skips the ones that carry no
             fact, which is what an unmarked square is. */
          const speaks = day.state !== "NO_REGISTER";
          const open = selected === day.key;

          const face = (
            <>
              {/* The month, on its first day only — "8" beside "25" is
                  unreadable when one is August and the other July. */}
              {day.monthLabel ? (
                <span className="text-[10px] leading-none font-bold opacity-80">
                  {day.monthLabel}
                </span>
              ) : null}
              <span className="text-dash-xs leading-tight font-bold">
                {day.dayOfMonth}
              </span>
              {/* 🔴 SHAPE, NOT ONLY COLOUR — §12. An absence is the one state a
                  parent acts on, so it is the one that carries a mark as well
                  as a fill; the legend below turns every colour into words for
                  the rest. */}
              <span
                aria-hidden="true"
                className="mt-0.5 h-1.5 w-1.5 rounded-full"
                style={{
                  background: tone.dot ? "currentColor" : "transparent",
                }}
              />
            </>
          );

          /*
           * ⚠️ THE OPEN SQUARE IS RINGED, NOT RECOLOURED. Its fill is the state
           * it is in — that is the whole vocabulary the legend teaches — so
           * marking the selection with a colour would make one square lie about
           * a child's attendance for as long as the panel is open. A ring is
           * outside the fill and says only "this is the one below".
           */
          const shell = `play-day flex aspect-square w-full flex-col items-center justify-center rounded-lg border font-dash tabular ${
            day.state === "ABSENT" ? "play-day-on" : ""
          }${open ? " outline-2 outline-offset-2 outline-ink" : ""}`;

          const style = {
            /* The stagger runs along the row, so a week arrives as a week. */
            "--rise-i": i % 7,
            background: tone.bg,
            borderColor: tone.line,
            color: tone.fg,
          } as CSSProperties;

          return (
            <li key={day.key}>
              {onPick ? (
                /*
                 * 🔴 A REAL `<button>`, NOT A CLICKABLE SPAN. It is reachable by
                 * keyboard, it announces itself as a control, and `aria-pressed`
                 * tells a screen-reader user which day the panel below is about
                 * — none of which a `<span onClick>` gives, on a grid whose whole
                 * new purpose is that it can be operated.
                 *
                 * ⚠️ AND IT KEEPS ITS ACCESSIBLE NAME EVEN WHEN THE SQUARE SAYS
                 * NOTHING. A silent `NO_REGISTER` square is right for a static
                 * grid and wrong for a button: a control with no name is a
                 * control a screen reader cannot offer. `describe` answers "no
                 * register was taken" for exactly this case.
                 */
                <button
                  type="button"
                  className={`${shell} cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink`}
                  title={describe(day)}
                  aria-label={describe(day)}
                  aria-pressed={open}
                  style={style}
                  onClick={() => {
                    onPick(day.key);
                  }}
                >
                  {face}
                </button>
              ) : (
                <span
                  className={shell}
                  /* 🔴 THE TOOLTIP AND THE ACCESSIBLE NAME ARE THE SAME SENTENCE.
                     A square is a shape; what it stands for has to be recoverable
                     without one, and a screen reader gets the sentences the old
                     table's rows held. */
                  title={describe(day)}
                  aria-label={speaks ? describe(day) : undefined}
                  aria-hidden={speaks ? undefined : "true"}
                  style={style}
                >
                  {face}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        🔴 THE LEGEND IS WHERE THE COLOURS BECOME CLAIMS, so each one is a
        sentence rather than a word. "Present" is only on the list because a
        teacher pressing save now records that the register was taken; the fourth
        row is the one that keeps the other three honest, and it is deliberately
        not phrased as an apology — a day nobody checked is an ordinary thing and
        the school can be asked about it.
      */}
      <p className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 font-dash text-dash-xs text-muted">
        {(["PRESENT", "ABSENT", "HOLIDAY", "NO_REGISTER"] as const).map(
          (state) => (
            <span key={state} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-3.5 w-3.5 rounded border"
                style={{
                  background: DAY_TONE[state].bg,
                  borderColor: DAY_TONE[state].line,
                }}
              />
              {state === "PRESENT"
                ? t.attendanceLegendPresent
                : state === "ABSENT"
                  ? t.attendanceLegendAbsent
                  : state === "HOLIDAY"
                    ? t.attendanceLegendHoliday
                    : /* 🔴 "Nobody took the register" — never "Present". */
                      t.attendanceLegendUnknown}
            </span>
          ),
        )}
      </p>

      {calendar.earlier.length > 0 && (
        /* The absences the grid cannot reach — see `attendanceCalendar.ts`.
           Without them the count tile and the grid would disagree on screen. */
        <p className="mt-3 flex flex-wrap items-center gap-2 font-dash text-dash-xs text-muted">
          {t.attendanceEarlier}
          {calendar.earlier.map((d) => (
            <StatusPill
              key={d.key}
              tone="warning"
              icon={<InfoIcon size={14} />}
            >
              {d.label}
            </StatusPill>
          ))}
        </p>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────── the month calendar ── */

export interface AttendanceMonthGridProps {
  month: AttendanceMonth;
  lang: Lang;
  /** Every square that carries a fact opens its day. */
  onPick: (day: string) => void;
  /** The open day, so the grid can ring the square the dialogue belongs to. */
  selected?: string | null;
}

/**
 * One month of squares — what the arrows walk through now.
 *
 * 🔴 IT IS THE SAME VOCABULARY AS `AttendanceGrid`, DELIBERATELY. `DAY_TONE` and
 * the legend below are shared with the rolling window, because a parent who has
 * learnt that coral means "marked absent" on one screen must not have to learn
 * it again on the other. What this grid adds is the two states a month has and a
 * rolling window cannot: a day that has not happened, and a day the response
 * never covered.
 *
 * 🔴 NEITHER OF THOSE IS DRAWN AS A STATE. A future square is an outline with a
 * numeral in it and no fill, and it is not a button — there is nothing to open.
 * An out-of-window square is the same, with its own line in the legend. Filling
 * either with `NO_REGISTER`'s tone would say "nobody took the register" about the
 * 30th of a month on the 12th.
 */
export function AttendanceMonthGrid({
  month,
  lang,
  onPick,
  selected = null,
}: AttendanceMonthGridProps) {
  const t = PARENT_COPY[lang];

  const describe = (day: AttendanceMonth["days"][number]): string => {
    if (day.future) return day.label;
    if (day.outsideWindow) return day.label;
    switch (day.state) {
      case "ABSENT":
        return t.attendanceAbsentOn(day.label);
      case "PRESENT":
        return t.attendancePresentOn(day.label);
      case "HOLIDAY":
        return t.attendanceHolidayOn(
          day.label,
          day.holidayName ?? t.attendanceLegendHoliday,
        );
      default:
        return t.attendanceUnknownOn(day.label);
    }
  };

  return (
    <div className="w-full">
      {/* Decorative: every square carries its own full date, so reading these
          letters out would announce the same information twice. */}
      <div
        aria-hidden="true"
        className="mb-1.5 grid grid-cols-7 gap-1 sm:gap-1.5 text-center font-dash text-dash-xs font-bold text-muted"
      >
        {month.weekdays.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>

      <ul className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {month.cells.map((day, i) => {
          if (day === null) {
            /* Padding. It is not a day, so it is not announced and not tappable. */
            return <li key={`pad-${String(i)}`} aria-hidden="true" />;
          }

          const known = !day.future && !day.outsideWindow;
          const tone = DAY_TONE[day.state];
          const open = selected === day.key;

          const shell = `play-day flex aspect-square w-full flex-col items-center justify-center rounded-lg border font-dash tabular ${
            known && day.state === "ABSENT" ? "play-day-on" : ""
          }${open ? " outline-2 outline-offset-2 outline-ink" : ""}${
            day.isToday ? " ring-2 ring-primary/60" : ""
          }`;

          const style = known
            ? ({
                "--rise-i": i % 7,
                background: tone.bg,
                borderColor: tone.line,
                color: tone.fg,
              } as CSSProperties)
            : ({
                /* No fill and a dashed edge: an outline is not a claim. */
                background: "transparent",
                borderColor: "var(--color-line)",
                borderStyle: "dashed",
                color: "var(--color-muted)",
              } as CSSProperties);

          const face = (
            <>
              <span className="text-dash-xs leading-tight font-bold">
                {day.dayOfMonth}
              </span>
              {/* 🔴 SHAPE, NOT ONLY COLOUR — §12. An absence is the one state a
                  parent acts on, so it carries a mark as well as a fill. */}
              <span
                aria-hidden="true"
                className="mt-0.5 h-1.5 w-1.5 rounded-full"
                style={{
                  background:
                    known && tone.dot ? "currentColor" : "transparent",
                }}
              />
            </>
          );

          return (
            <li key={day.key}>
              {known ? (
                <button
                  type="button"
                  className={`${shell} cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink`}
                  title={describe(day)}
                  aria-label={describe(day)}
                  aria-pressed={open}
                  style={style}
                  onClick={() => {
                    onPick(day.key);
                  }}
                >
                  {face}
                </button>
              ) : (
                /* ⚠️ A `<span>`, NOT A DISABLED BUTTON. A disabled control tells
                   a parent the product has something here it is refusing them;
                   there is simply nothing to open on a day that has not
                   happened. */
                <span
                  className={shell}
                  title={describe(day)}
                  aria-hidden="true"
                  style={style}
                >
                  {face}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        The legend, and the fifth row is the one months added.

        🔴 EVERY COLOUR IS A SENTENCE. "Present" is only on the list because a
        teacher pressing save records that the register was taken; the last two
        rows are what keep the other three honest.
      */}
      <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 font-dash text-dash-xs text-muted">
        {(["PRESENT", "ABSENT", "HOLIDAY", "NO_REGISTER"] as const).map(
          (state) => (
            <span key={state} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-3.5 w-3.5 rounded border"
                style={{
                  background: DAY_TONE[state].bg,
                  borderColor: DAY_TONE[state].line,
                }}
              />
              {state === "PRESENT"
                ? t.attendanceLegendPresent
                : state === "ABSENT"
                  ? t.attendanceLegendAbsent
                  : state === "HOLIDAY"
                    ? t.attendanceLegendHoliday
                    : t.attendanceLegendUnknown}
            </span>
          ),
        )}
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block h-3.5 w-3.5 rounded border border-dashed border-line"
          />
          Not yet, or before this record starts
        </span>
      </p>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── the sticker shelf ── */

/*
 * ✅ IT MOVED TO `Rewards.tsx`, AND THE MOVE IS THE FIX RATHER THAN A TIDY-UP.
 *
 * 🔴 WHAT STOOD HERE RENDERED THE RAW ID. It was a wrapping row of
 * `StatusPill`s whose text was the string the server stores — `sky-quest-l2`,
 * `letter-lagoon-l1` — so the one screen in this product that is about a child's
 * reward printed database slugs at their family. The note above it worried about
 * whether a sticker should wobble; the label was unreadable.
 *
 * `stickerBook.ts` turns the id back into a game and a level, and the shelf that
 * draws it needs the game's plate and its localised name — which is a card, not
 * a pill. It lives beside the coins-and-stickers explainer because a parent who
 * has just been told what a sticker IS is the one looking at the shelf.
 *
 * ⚠️ THE ONE RULE THAT TRAVELLED WITH IT: a sticker is still not a control. It
 * opens nothing, and making it a button would promise a screen that does not
 * exist. `--ease-kid`'s overshoot still appears there and on the coin and
 * nowhere else in the tier.
 */
